import { useState } from 'react';
import { api } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';

type Mover = { symbol: string; name: string; price: number; change: number; pct: number; volume: number };
type Data = {
  gainers: Mover[]; losers: Mover[]; volume: Mover[]; as_of: string; count: number; index: string; open: boolean;
};

const px = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * A look at the prices behind Market Movers in News & Analysis. They are
 * fetched by themselves while the market is open; nothing here is edited.
 * Fetch now is for checking the feed is alive.
 */
export function MarketMoversAdmin() {
  const { data, error, reload } = useAsync((signal) => api.get<Data>('/movers', signal));
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  async function refresh() {
    setBusy(true); setNote('');
    try {
      const r = await api.post<{ report: { ok: number; failed: number } }>('/movers/refresh', {});
      setNote(`${r.report.ok} prices updated${r.report.failed ? `, ${r.report.failed} could not be read` : ''}.`);
      await reload();
    } catch (e) { setNote((e as Error).message); }
    setBusy(false);
  }

  const list = (title: string, rows: Mover[]) => (
    <div className="crd" style={{ padding: '18px 20px' }}>
      <div className="kh">{title}</div>
      {rows.map((r) => (
        <div key={r.symbol} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 0',
          borderTop: '1px solid var(--line)', fontSize: 13 }}>
          <span><b>{r.name}</b> <span className="muted">{r.symbol}</span></span>
          <span style={{ fontVariantNumeric: 'tabular-nums', color: r.change >= 0 ? 'var(--up)' : 'var(--down)' }}>
            {px(r.price)} · {r.change >= 0 ? '+' : ''}{r.pct.toFixed(2)}%
          </span>
        </div>
      ))}
      {rows.length === 0 ? <p className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>Nothing yet.</p> : null}
    </div>
  );

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Market Movers</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7, maxWidth: 680, lineHeight: 1.55 }}>
            {data ? `${data.count} stocks tracked (${data.index}). ${data.open ? 'The market is open, so prices refresh every 5 minutes.' : 'The market is closed, so these are the last prices.'}` : '—'}
            {' '}Prices come from a free public feed and can be delayed. Treat this as a prototype until a licensed feed is chosen.
          </p>
        </div>
        <button className="b bg" type="button" disabled={busy} onClick={() => void refresh()}>{busy ? 'Fetching…' : 'Fetch now'}</button>
      </div>
      {error ? <div className="err">{String((error as Error).message)}</div> : null}
      {note ? <div className="crd" style={{ padding: '10px 16px', marginBottom: 12, fontSize: 12.5 }}>{note}</div> : null}
      {data ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 16 }}>
          {list('Top gainers', data.gainers.slice(0, 5))}
          {list('Top losers', data.losers.slice(0, 5))}
          {list('Most active', data.volume.slice(0, 5))}
        </div>
      ) : null}
    </div>
  );
}
